import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import api from '../../../services/authService'
import { toast } from 'react-hot-toast'
import { 
  ArrowLeftIcon, 
  PlusIcon, 
  TrashIcon, 
  DocumentTextIcon, 
  CubeIcon, 
  ListBulletIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline'

export default function PaginaConfiguracionSimulacion() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [guardando, setGuardando] = useState(false)

  // Estado del formulario
  const [titulo, setTitulo] = useState('')
  const [descripcion, setDescripcion] = useState('')
  const [nivelDificultad, setNivelDificultad] = useState('intermedio')
  const [tipo, setTipo] = useState('Despacho')
  
  // Estado para datos_caso
  const [mercancia, setMercancia] = useState({
    descripcion: '',
    cantidad: 0,
    peso_bruto: 0,
    peso_neto: 0,
    valor_factura: 0,
    moneda: 'USD',
    pais_origen: '',
    pais_vendedor: ''
  })
  
  const [documentos, setDocumentos] = useState<string[]>([])
  const [nuevoDocumento, setNuevoDocumento] = useState('')
  
  const [etapas, setEtapas] = useState<any[]>([])

  // Cargar datos
  useEffect(() => {
    const cargarSimulacion = async () => {
      try {
        const res = await api.get(`/api/simulaciones/${id}`)
        const sim = res.data
        setTitulo(sim.titulo)
        setDescripcion(sim.descripcion || '')
        setNivelDificultad(sim.nivel_dificultad)
        setTipo(sim.tipo)
        
        if (sim.datos_caso) {
          if (sim.datos_caso.mercancia) setMercancia(sim.datos_caso.mercancia)
          if (sim.datos_caso.documentos) setDocumentos(sim.datos_caso.documentos)
          if (sim.datos_caso.etapas) setEtapas(sim.datos_caso.etapas)
        }
      } catch (error) {
        toast.error('Error al cargar el caso práctico')
        console.error(error)
      } finally {
        setLoading(false)
      }
    }
    cargarSimulacion()
  }, [id])

  const guardarCambios = async () => {
    setGuardando(true)
    try {
      const datos = {
        titulo,
        descripcion,
        tipo,
        nivel_dificultad: nivelDificultad,
        datos_caso: {
          mercancia,
          documentos,
          etapas
        }
      }
      
      await api.put(`/api/simulaciones/${id}`, datos)
      toast.success('Caso práctico actualizado correctamente')
      navigate(-1)
    } catch (error) {
      toast.error('Error al guardar cambios')
      console.error(error)
    } finally {
      setGuardando(false)
    }
  }

  // Helpers para etapas
  const agregarEtapa = () => {
    setEtapas([...etapas, { id: etapas.length + 1, nombre: '', descripcion: '' }])
  }

  const actualizarEtapa = (index: number, campo: string, valor: string) => {
    const nuevasEtapas = [...etapas]
    nuevasEtapas[index] = { ...nuevasEtapas[index], [campo]: valor }
    setEtapas(nuevasEtapas)
  }

  const eliminarEtapa = (index: number) => {
    const nuevasEtapas = etapas.filter((_, i) => i !== index)
    setEtapas(nuevasEtapas)
  }

  // Helpers para documentos
  const agregarDocumento = () => {
    if (nuevoDocumento.trim()) {
      setDocumentos([...documentos, nuevoDocumento.trim()])
      setNuevoDocumento('')
    }
  }

  const eliminarDocumento = (index: number) => {
    setDocumentos(documentos.filter((_, i) => i !== index))
  }

  if (loading) return <div className="p-8 text-center">Cargando configuración...</div>

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button 
          onClick={() => navigate(-1)}
          aria-label="Volver a la pantalla anterior"
          className="flex items-center text-gray-500 hover:text-gray-700 dark:text-gray-400"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Volver
        </button>
        <button
          onClick={guardarCambios}
          disabled={guardando}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {guardando ? 'Guardando...' : 'Guardar Cambios'}
          <CheckCircleIcon className="h-5 w-5 ml-2" />
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6 space-y-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Configuración del Caso Práctico</h1>
        
        {/* Datos Básicos */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="col-span-2">
            <label htmlFor="simulacion-config-titulo" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Título</label>
            <input
              id="simulacion-config-titulo"
              name="titulo"
              type="text"
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm p-2 border"
            />
          </div>
          
          <div className="col-span-2">
            <label htmlFor="simulacion-config-descripcion" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Descripción</label>
            <textarea
              id="simulacion-config-descripcion"
              name="descripcion"
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              rows={3}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm p-2 border"
            />
          </div>

          <div>
            <label htmlFor="simulacion-config-dificultad" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Nivel de Dificultad</label>
            <select
              id="simulacion-config-dificultad"
              name="nivelDificultad"
              value={nivelDificultad}
              onChange={(e) => setNivelDificultad(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm p-2 border"
            >
              <option value="básico">Básico</option>
              <option value="intermedio">Intermedio</option>
              <option value="avanzado">Avanzado</option>
            </select>
          </div>
        </div>
      </div>

      {/* Mercancía */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6 space-y-6">
        <div className="flex items-center gap-2 mb-4">
          <CubeIcon className="h-6 w-6 text-blue-500" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Detalles de la Mercancía</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="col-span-2">
            <label htmlFor="simulacion-config-mercancia-descripcion" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Descripción de Mercancía</label>
            <input
              id="simulacion-config-mercancia-descripcion"
              name="mercanciaDescripcion"
              type="text"
              value={mercancia.descripcion}
              onChange={(e) => setMercancia({...mercancia, descripcion: e.target.value})}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm p-2 border"
            />
          </div>
          <div>
            <label htmlFor="simulacion-config-pais-origen" className="block text-sm font-medium text-gray-700 dark:text-gray-300">País Origen (Código)</label>
            <input
              id="simulacion-config-pais-origen"
              name="paisOrigen"
              type="text"
              value={mercancia.pais_origen}
              onChange={(e) => setMercancia({...mercancia, pais_origen: e.target.value})}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm p-2 border"
              placeholder="Ej: CHN"
            />
          </div>
          <div>
            <label htmlFor="simulacion-config-valor-factura" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Valor Factura</label>
            <input
              id="simulacion-config-valor-factura"
              name="valorFactura"
              type="number"
              value={mercancia.valor_factura}
              onChange={(e) => setMercancia({...mercancia, valor_factura: Number(e.target.value)})}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm p-2 border"
            />
          </div>
        </div>
      </div>

      {/* Etapas */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6 space-y-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <ListBulletIcon className="h-6 w-6 text-blue-500" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Etapas de la Simulación</h2>
          </div>
          <button
            onClick={agregarEtapa}
            className="text-sm bg-blue-50 text-blue-600 px-3 py-1 rounded-md hover:bg-blue-100 flex items-center"
          >
            <PlusIcon className="h-4 w-4 mr-1" /> Agregar Etapa
          </button>
        </div>
        
        <div className="space-y-4">
          {etapas.map((etapa, index) => (
            <div key={index} className="flex gap-4 p-4 border rounded-lg bg-gray-50 dark:bg-gray-700/50 dark:border-gray-600">
              <span className="flex items-center justify-center h-8 w-8 rounded-full bg-blue-100 text-blue-600 font-bold shrink-0">
                {index + 1}
              </span>
              <div className="flex-1 space-y-3">
                <input
                  id={`simulacion-config-etapa-nombre-${index}`}
                  name={`etapaNombre-${index}`}
                  aria-label={`Nombre de la etapa ${index + 1}`}
                  type="text"
                  placeholder="Nombre de la etapa"
                  value={etapa.nombre}
                  onChange={(e) => actualizarEtapa(index, 'nombre', e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm p-2 border"
                />
                <input
                  id={`simulacion-config-etapa-descripcion-${index}`}
                  name={`etapaDescripcion-${index}`}
                  aria-label={`Descripción de la etapa ${index + 1}`}
                  type="text"
                  placeholder="Descripción de la tarea"
                  value={etapa.descripcion}
                  onChange={(e) => actualizarEtapa(index, 'descripcion', e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm p-2 border"
                />
              </div>
              <button 
                onClick={() => eliminarEtapa(index)}
                aria-label={`Eliminar etapa ${index + 1}`}
                className="text-red-500 hover:text-red-700 self-start"
              >
                <TrashIcon className="h-5 w-5" />
              </button>
            </div>
          ))}
          {etapas.length === 0 && (
            <p className="text-center text-gray-500 py-4">No hay etapas definidas. Agrega una para comenzar.</p>
          )}
        </div>
      </div>

      {/* Documentos */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6 space-y-6">
        <div className="flex items-center gap-2 mb-4">
          <DocumentTextIcon className="h-6 w-6 text-blue-500" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Documentos Requeridos</h2>
        </div>
        
        <div className="flex gap-2 mb-4">
          <input
            id="simulacion-config-documento-nuevo"
            name="nuevoDocumento"
            aria-label="Nuevo documento requerido"
            type="text"
            placeholder="Nuevo documento (ej: Factura Comercial)"
            value={nuevoDocumento}
            onChange={(e) => setNuevoDocumento(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && agregarDocumento()}
            className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm p-2 border"
          />
          <button
            onClick={agregarDocumento}
            className="bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200"
          >
            Agregar
          </button>
        </div>

        <div className="flex flex-wrap gap-2">
          {documentos.map((doc, index) => (
            <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
              {doc}
              <button
                onClick={() => eliminarDocumento(index)}
                aria-label={`Eliminar documento ${doc}`}
                className="ml-2 text-blue-600 hover:text-blue-800 dark:text-blue-300 dark:hover:text-blue-100"
              >
                ×
              </button>
            </span>
          ))}
          {documentos.length === 0 && (
            <p className="text-sm text-gray-500">No hay documentos agregados.</p>
          )}
        </div>
      </div>
    </div>
  )
}
